import React, { useState } from 'react';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdSortByAlpha,
  MdSwapVert,
} from 'react-icons/md';
import {
  ChipMenu,
  Divider,
  DIVIDER_ORIENTATION_VERTICAL,
  IconButton,
  SearchInput,
  SegmentedControl,
  type SegmentedControlOption,
} from '@aibuild/ui';
import type {
  ProcessListSort,
  ProcessListStatusFilter,
} from '@api/processes';
import {
  SortAnchor,
  SortMenuPosition,
  SortTrigger,
  ToolbarRoot,
  ToolbarSpacer,
} from './ProcessesToolbar.styled';

export interface ProcessesToolbarCounts {
  all: number | undefined;
  published: number | undefined;
  draft: number | undefined;
}

export interface ProcessesToolbarProps {
  search: string;
  onSearchChange: (next: string) => void;
  status: ProcessListStatusFilter;
  onStatusChange: (next: ProcessListStatusFilter) => void;
  sort: ProcessListSort;
  onSortChange: (next: ProcessListSort) => void;
  onCreate: () => void;
  isCreating?: boolean;
  /** Per-status totals shown next to each segment label (e.g. "Drafts 12"). */
  counts?: ProcessesToolbarCounts;
  dataTestId?: string;
}

interface SortOption {
  id: ProcessListSort;
  label: string;
  icon: React.ReactNode;
}

// Tuple-typed (note the leading `SortOption,`) so TS narrows
// `SORT_OPTIONS[0]` to `SortOption` rather than `SortOption | undefined`.
// That lets the `find(...) ?? SORT_OPTIONS[0]` expression below type-check
// without a non-null assertion (Sonar `no-non-null-assertion`).
const SORT_OPTIONS: readonly [SortOption, ...SortOption[]] = [
  { id: 'newest', label: 'Newest', icon: <MdArrowDownward /> },
  { id: 'oldest', label: 'Oldest', icon: <MdArrowUpward /> },
  { id: 'name', label: 'Name A→Z', icon: <MdSortByAlpha /> },
];

/**
 * Build segmented-control options including count badges when the parent has
 * resolved them. While counts are still loading, the segments render labels
 * only (the segmented control hides the badge for `count === undefined`).
 */
const buildStatusOptions = (
  counts: ProcessesToolbarCounts | undefined,
): SegmentedControlOption[] => [
  { id: 'all', label: 'All', count: counts?.all },
  { id: 'published', label: 'Published', count: counts?.published },
  { id: 'draft', label: 'Drafts', count: counts?.draft },
];

const ProcessesToolbar: React.FC<ProcessesToolbarProps> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  sort,
  onSortChange,
  onCreate,
  isCreating = false,
  counts,
  dataTestId = 'processes-toolbar',
}) => {
  const [sortOpen, setSortOpen] = useState(false);
  // SORT_OPTIONS is a non-empty readonly tuple, so the fallback `[0]` is
  // always defined — no non-null assertion needed (Sonar).
  const activeSort =
    SORT_OPTIONS.find((o) => o.id === sort) ?? SORT_OPTIONS[0];

  return (
    <ToolbarRoot data-testid={dataTestId}>
      <SearchInput
        value={search}
        onChange={onSearchChange}
        placeholder="Search"
        aria-label="Search processes"
        dataTestId={`${dataTestId}__search`}
      />
      <SegmentedControl
        options={buildStatusOptions(counts)}
        value={status}
        onChange={(next) => onStatusChange(next as ProcessListStatusFilter)}
        ariaLabel="Filter processes by status"
        dataTestId={`${dataTestId}__status`}
      />
      <Divider
        orientation={DIVIDER_ORIENTATION_VERTICAL}
        dataTestId={`${dataTestId}__divider`}
      />
      <SortAnchor data-testid={`${dataTestId}__sort`}>
        <SortTrigger
          type="button"
          $isOpen={sortOpen}
          $isActive={sort !== 'newest'}
          onClick={() => setSortOpen((p) => !p)}
          aria-haspopup="menu"
          aria-expanded={sortOpen}
          title={`Sort: ${activeSort.label}`}
          data-testid={`${dataTestId}__sort-trigger`}
        >
          <MdSwapVert aria-hidden="true" />
          <span>{activeSort.label}</span>
        </SortTrigger>
        {sortOpen && (
          <SortMenuPosition>
            <ChipMenu
              onClose={() => setSortOpen(false)}
              ariaLabel="Sort processes"
              dataTestId={`${dataTestId}__sort-menu`}
            >
              <ChipMenu.List>
                {SORT_OPTIONS.map((o) => (
                  <ChipMenu.Item
                    key={o.id}
                    icon={o.icon}
                    isActive={o.id === sort}
                    onClick={() => {
                      onSortChange(o.id);
                      setSortOpen(false);
                    }}
                    dataTestId={`${dataTestId}__sort-option-${o.id}`}
                  >
                    {o.label}
                  </ChipMenu.Item>
                ))}
              </ChipMenu.List>
            </ChipMenu>
          </SortMenuPosition>
        )}
      </SortAnchor>
      <ToolbarSpacer />
      <IconButton
        ariaLabel="New process"
        icon={<MdAdd size={22} />}
        variant="filled"
        onClick={onCreate}
        disabled={isCreating}
        dataTestId={`${dataTestId}__new`}
      />
    </ToolbarRoot>
  );
};

export default ProcessesToolbar;
